<template>
  <div class="page">
    <div class="auth-card">
      <h2>Create Your Account</h2>
      <form @submit.prevent="signup" class="signup-form">
        <label for="email">Email</label>
        <input
            id="email"
            type="email"
            v-model="email"
            placeholder="Email"
            required
            ref="emailInput"
            :disabled="isLoading"
            @input="errorMessage = ''"
        />

        <label for="password">Password</label>
        <input
            id="password"
            type="password"
            v-model="password"
            placeholder="Password"
            required
            :disabled="isLoading"
            @input="errorMessage = ''"
        />

        <div class="terms">
          <input type="checkbox" id="terms" v-model="agreedToTerms" :disabled="isLoading" />
          <label for="terms">
            I agree to the
            <a href="/terms-of-service" target="_blank">terms and conditions</a>.
          </label>
        </div>

        <button type="submit" :disabled="isLoading || !agreedToTerms">
          <span v-if="isLoading" class="spinner" aria-label="Creating account..."></span>
          <span v-else>Create Account</span>
        </button>
      </form>

      <p class="login-message">
        Already have an account?
        <router-link to="/login">Log in</router-link>
      </p>

      <transition name="fade">
        <p v-if="errorMessage" class="error-message" role="alert">{{ errorMessage }}</p>
      </transition>
    </div>
  </div>
</template>

<script>
import { auth } from '@/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';

export default {
  name: 'SignUpPage',
  data() {
    return {
      email: '',
      password: '',
      agreedToTerms: false,
      errorMessage: '',
      isLoading: false
    };
  },
  mounted() {
    this.$refs.emailInput.focus();
  },
  methods: {
    async signup() {
      this.errorMessage = '';
      this.isLoading = true;
      try {
        await createUserWithEmailAndPassword(auth, this.email, this.password);
        this.$router.push('/profile?welcome=true');
      } catch (error) {
        const code = error.code || '';
        if (code === 'auth/email-already-in-use') {
          this.errorMessage = 'An account with this email already exists.';
        } else if (code === 'auth/weak-password') {
          this.errorMessage = 'Password is too weak. Please use at least 6 characters.';
        } else if (code === 'auth/invalid-email') {
          this.errorMessage = 'Please enter a valid email address.';
        } else if (code === 'auth/network-request-failed') {
          this.errorMessage = 'Unable to connect. Please check your internet connection.';
        } else {
          this.errorMessage = 'Something went wrong. Please try again later.';
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.page {
  padding: 50px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.auth-card {
  width: 100%;
  max-width: 420px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 36px 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  text-align: center;
  animation: cardIn 0.5s ease both;
}

@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

h2 {
  color: var(--hollywood-cerise);
  margin: 0 0 24px;
  font-size: 1.6rem;
}

.signup-form {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

label {
  font-weight: 600;
  margin: 0 0 6px;
  text-align: left;
  font-size: 0.9rem;
  color: var(--rich-black);
}

input {
  width: 100%;
  padding: 10px 12px;
  background-color: white;
  color: var(--rich-black);
  border: 1px solid #ccc;
  border-radius: 8px;
  box-sizing: border-box;
  margin-bottom: 16px;
  font-size: 0.95rem;
}

input:focus {
  outline: none;
  border-color: var(--hollywood-cerise);
  box-shadow: 0 0 0 3px rgba(228, 19, 147, 0.1);
}

.terms {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  text-align: left;
}

.terms input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.terms label {
  margin: 0;
  font-size: 0.85rem;
}

.terms a {
  color: var(--hollywood-cerise);
  text-decoration: none;
}

.terms a:hover {
  text-decoration: underline;
}

button[type="submit"] {
  margin-top: 4px;
  padding: 12px 20px;
  color: white;
  background-color: var(--hollywood-cerise);
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

button[type="submit"]:hover {
  background-color: var(--violet-lush);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(228, 19, 147, 0.25);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.login-message {
  margin-top: 16px;
  font-size: 0.9rem;
  color: var(--violet-lush);
}

.login-message a {
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-weight: 600;
}

.login-message a:hover {
  text-decoration: underline;
}

.error-message {
  color: red;
  font-size: 0.9rem;
  margin-top: 16px;
  line-height: 1.6;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .page {
    padding: 30px 12px;
  }

  .auth-card {
    padding: 24px 20px;
  }

  h2 {
    font-size: 1.4rem;
  }
}
</style>
